import { useMemo, useState, useEffect, useCallback } from 'react';
import { prop } from 'ramda';
import { useNavigate } from '@theme/js/hooks/navigate';
import { useLaunchGame } from '@core/js/hooks/gameLauncher';
import { useTranslations } from '@core/js/hooks/i18n';
import { useShowNotification } from '@core/js/hooks/useShowNotification';
import { useGameConfigurationSelector } from '@core/js/store/hooks';
import { useAssetRedirectData } from '@theme/js/components/common/Game/hooks/useAssetRedirectData';

import * as Types from '../types';

export const useGameHandler = () => {
    const [gameIdentifier, setGameIdentifier] = useState<Types.GameIdentifier>({
        gameCode: null,
        launchAlias: null
    });
    const [shouldTrigger, setShouldTrigger] = useState<boolean>(false);
    const navigate = useNavigate();
    const launchGame = useLaunchGame();
    const games = useGameConfigurationSelector(prop('games'));
    const showNotification = useShowNotification();
    const translate = useTranslations('playtech.mobile.system.ucip.');

    const finalGameCode = useMemo(() => {
        if (gameIdentifier.gameCode && games[gameIdentifier.gameCode]) {
            return gameIdentifier.gameCode;
        }

        if (gameIdentifier.launchAlias) {
            const foundGame = Object.values(games)?.find(
                (game: any) => game?.liveLaunchAlias === gameIdentifier.launchAlias)?.gameCode;
            return foundGame || '';
        }

        return '';
    }, [gameIdentifier, games]);

    const { isArticleIdPathPresent, linkToPage } = useAssetRedirectData(finalGameCode);
    const redirectData: Types.IRedirectData = useMemo(
        () => ({ isArticleIdPathPresent, linkToPage }),
        [isArticleIdPathPresent, linkToPage]
    );

    const notifyError = useCallback(
        (messageKey: string) => {
            showNotification(translate(messageKey), {
                variant: 'error',
                autoHideDuration: 5
            });
        },
        [showNotification, translate]
    );

    useEffect(() => {
        if (!shouldTrigger) { return; }

        if (!finalGameCode) {
            notifyError('gameLaunch.gameNotFound');
            navigate('/');
            setShouldTrigger(false);
            return;
        }

        if (redirectData.isArticleIdPathPresent) {
            if (!redirectData.linkToPage?.trim()) {
                notifyError('gameLaunch.linkToPage.notFound');
                navigate('/');
                setShouldTrigger(false);

                return;
            }

            navigate(redirectData.linkToPage);
        } else {
            launchGame({ code: finalGameCode, mode: 1 });
            navigate('/');
        }

        setShouldTrigger(false);
    }, [shouldTrigger, finalGameCode, redirectData, navigate, launchGame, notifyError]);

    const handleGameLaunch = useCallback(
        (gameCode?: string | null, launchAlias?: string | null) => {
            if (!gameCode && !launchAlias) {
                notifyError('gameLaunch.missing-required-parameters');
                navigate('/');
                return;
            }

            setGameIdentifier({ gameCode, launchAlias });
            setShouldTrigger(true);
        },
        [notifyError, navigate]
    );

    return { handleGameLaunch };
};
